import {
  InlineRovoPromptTriggerSource,
  inlineRovoPromptState
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { type PopperProps, usePopper } from 'react-popper';
import { useSnapshot } from 'valtio';

type PopperSettings = {
  placement: PopperProps<unknown>['placement'];
  referenceElement: HTMLElement | undefined;
  padding: number | { top: number; right: number; bottom: number; left: number };
};

const POPPER_PADDING_DEFAULT = 8;
const POPPER_PADDING_TABLE_ENTRY = { top: 64, right: 8, bottom: 96, left: 32 };
const POPPER_PADDING_TABLE_FIELD = { ...POPPER_PADDING_TABLE_ENTRY, top: 0 };
const POPPER_PADDING_VIEW_MENU = { top: -32, right: 0, bottom: 0, left: 32 };

/** Helper function to get the last selected item matching a selector within a given container. */
function getLastSelectedItemWithSelector(
  container: HTMLElement,
  selector: string
): HTMLElement | undefined {
  if (!container) {
    return undefined;
  }

  const selectedItems = Array.from(container.querySelectorAll(selector)) as HTMLElement[];
  return selectedItems.pop();
}

const getLastSelectedTableRow = (container: HTMLElement) =>
  getLastSelectedItemWithSelector(container, `tr.${TABLE_CLASSNAMES.IS_SELECTED}`);
const getLastSelectedTableCell = (container: HTMLElement) =>
  getLastSelectedItemWithSelector(container, `td.${TABLE_CLASSNAMES.IS_SELECTED}`);
const getLastSelectedTableHeader = (container: HTMLElement) =>
  getLastSelectedItemWithSelector(container, `th.${TABLE_CLASSNAMES.IS_SELECTED}`);
const getLastSelectedBoardCard = (container: HTMLElement) =>
  getLastSelectedItemWithSelector(container, `.${TABLE_CLASSNAMES.IS_SELECTED}`);
const getLastSelectedGalleryCard = (container: HTMLElement) =>
  getLastSelectedItemWithSelector(container, `.${TABLE_CLASSNAMES.IS_SELECTED}`);

function getPopperSettingsForTableEntry(container: HTMLElement): PopperSettings {
  return {
    placement: 'bottom-start',
    referenceElement:
      getLastSelectedTableRow(container) || getLastSelectedTableCell(container),
    padding: POPPER_PADDING_TABLE_ENTRY
  };
}

function getPopperSettingsForTableField(container: HTMLElement): PopperSettings {
  return {
    placement: 'right-start',
    referenceElement: getLastSelectedTableHeader(container),
    padding: POPPER_PADDING_TABLE_FIELD
  };
}

function getPopperSettingsForBoardCard(container: HTMLElement): PopperSettings {
  return {
    placement: 'bottom-start',
    referenceElement: getLastSelectedBoardCard(container),
    padding: POPPER_PADDING_DEFAULT
  };
}

function getPopperSettingsForGalleryCard(container: HTMLElement): PopperSettings {
  return {
    placement: 'bottom-start',
    referenceElement: getLastSelectedGalleryCard(container),
    padding: POPPER_PADDING_DEFAULT
  };
}

function getPopperSettingsViewMenu(container: HTMLElement): PopperSettings {
  return {
    placement: 'top-start',
    referenceElement: container,
    padding: POPPER_PADDING_VIEW_MENU
  };
}

function getPopperSettingsForRichTextToolbar(container: HTMLElement): PopperSettings {
  // Position relative to the cell (not the row) so the prompt appears
  // at the correct column, not always at the first column.
  return {
    placement: 'bottom-start',
    referenceElement:
      getLastSelectedTableCell(container) || getLastSelectedTableRow(container),
    padding: POPPER_PADDING_TABLE_ENTRY
  };
}

const getPopperSettingsByTriggerSource: Record<
  InlineRovoPromptTriggerSource,
  (container: HTMLElement) => PopperSettings
> = {
  [InlineRovoPromptTriggerSource.TableEntry]: getPopperSettingsForTableEntry,
  [InlineRovoPromptTriggerSource.TableField]: getPopperSettingsForTableField,
  [InlineRovoPromptTriggerSource.BoardEntry]: getPopperSettingsForBoardCard,
  [InlineRovoPromptTriggerSource.GalleryEntry]: getPopperSettingsForGalleryCard,
  [InlineRovoPromptTriggerSource.ViewMenu]: getPopperSettingsViewMenu,
  [InlineRovoPromptTriggerSource.RichTextToolbar]: getPopperSettingsForRichTextToolbar
};

/**
 * Handles popper positioning for the inline Rovo prompt.
 * Dynamically positions the prompt based on where it was triggered from, and ensures it stays within the container bounds.
 *
 * @param popperElement The popper element (the inline Rovo prompt itself).
 * @param container The container element within which the prompt should be positioned.
 * @returns Popper attributes, styles, and reference element.
 */
export const useInlineRovoPromptPopper = (
  popperElement: HTMLElement | null,
  container: HTMLElement
) => {
  const { openedFrom } = useSnapshot(inlineRovoPromptState);
  const { padding, placement, referenceElement } =
    getPopperSettingsByTriggerSource[openedFrom](container);

  const popper = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      // Ensure prompt stays within the container bounds
      {
        name: 'preventOverflow',
        options: {
          boundary: container,
          altAxis: true,
          padding,
          tether: false
        }
      },
      // Flip the prompt to the opposite side if there's not enough space
      {
        name: 'flip',
        options: {
          boundary: container,
          flipVariations: false,
          padding: POPPER_PADDING_DEFAULT
        }
      },
      // Offset the prompt slightly from the reference element
      {
        name: 'offset',
        options: {
          offset: [0, 6]
        }
      }
    ]
  });

  return {
    ...popper,
    referenceElement
  };
};
